import { useState } from 'react';
import { FunnelIcon } from '@heroicons/react/24/outline';

export interface FilterState {
  userId?: string;
  startDate?: string;
  endDate?: string;
  activeOnly: boolean;
  sentimentFilter: 'All' | 'NeedsAttention';
}

interface FilterPanelProps {
  onFilterChange: (filters: FilterState) => void;
  users?: Array<{ id: string; displayName: string }>;
}

export default function FilterPanel({ onFilterChange, users = [] }: FilterPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    activeOnly: false,
    sentimentFilter: 'All',
  });

  const updateFilter = (key: keyof FilterState, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const emptyFilters: FilterState = { activeOnly: false, sentimentFilter: 'All' };
    setFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const hasActiveFilters = filters.userId || filters.startDate || filters.endDate || filters.activeOnly || filters.sentimentFilter !== 'All';

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle filters"
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
          hasActiveFilters
            ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
      >
        <FunnelIcon className="h-5 w-5" />
        Filters
        {hasActiveFilters && (
          <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800">
            Active
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Panel */}
          <div className="absolute right-0 z-20 mt-2 w-80 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Filter Conversations</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className="space-y-4">
                {/* Sentiment Filter */}
                <div>
                  <label htmlFor="sentiment-filter" className="block text-sm font-medium text-gray-700 mb-1">
                    Sentiment
                  </label>
                  <select
                    id="sentiment-filter"
                    value={filters.sentimentFilter}
                    onChange={(e) => updateFilter('sentimentFilter', e.target.value as 'All' | 'NeedsAttention')}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                  >
                    <option value="All">All conversations</option>
                    <option value="NeedsAttention">⚠️ Needs Attention (Negative)</option>
                  </select>
                </div>

                {/* User Filter */}
                {users.length > 0 && (
                  <div>
                    <label htmlFor="user-filter" className="block text-sm font-medium text-gray-700 mb-1">
                      User
                    </label>
                    <select
                      id="user-filter"
                      value={filters.userId || ''}
                      onChange={(e) => updateFilter('userId', e.target.value || undefined)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                    >
                      <option value="">All users</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Start Date */}
                <div>
                  <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="start-date"
                    value={filters.startDate || ''}
                    onChange={(e) => updateFilter('startDate', e.target.value || undefined)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                  />
                </div>

                {/* End Date */}
                <div>
                  <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    id="end-date"
                    value={filters.endDate || ''}
                    onChange={(e) => updateFilter('endDate', e.target.value || undefined)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                  />
                </div>

                {/* Active Only Checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="active-only"
                    checked={filters.activeOnly}
                    onChange={(e) => updateFilter('activeOnly', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="active-only" className="ml-2 block text-sm text-gray-700">
                    Active sessions only (last 24 hours)
                  </label>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
